import { c as _c } from "react/compiler-runtime";
// Kanban columns, each a booklet page (POC: section.col > h2 + .cards > .card). Cards are memoised with stable
// props, so selecting a task re-renders only the old and the new card; scroll positions and covers survive.
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { local, type Snapshot, type Task, type TaskStatus } from "@/api";
import { InstanceTag } from "@/components/instance-tag";
import { useBlobUrl } from "@/lib/blob";
import type { Column } from "@/lib/hooks";
import { useData } from "@/store";
import { Dot } from "@/components/dot";

// Columns and their cards (which ones, in manual order, blocked or not) are the core's board view (lib/hooks.ts
// useBoardView); this page only draws them.
export type { Column };
type ColumnProps = {
  snapshot: Snapshot;
  column: Column;
  /** Columns on the board, in order (Shift+←/→ moves a focused card one column). */
  statuses: TaskStatus[];
  selected: string | null;
  /** Must be stable (cards are memoised on it). */
  onOpen: (id: string) => void;
};

/** One kanban column = one booklet page. Drag mode (lib/drag.ts) works on the DOM of these. */
export function BoardColumn(t0) {
  const $ = _c(38);
  const {
    snapshot,
    column: t1,
    statuses,
    selected,
    onOpen
  } = t0;
  const {
    status,
    color,
    tasks
  } = t1;
  const {
    t
  } = useTranslation();
  let t2;
  if ($[0] !== snapshot.projects) {
    t2 = new Map(snapshot.projects.map(_temp));
    $[0] = snapshot.projects;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const projectName = t2;
  let t3;
  if ($[2] !== snapshot.persons) {
    t3 = new Map(snapshot.persons.map(_temp2));
    $[2] = snapshot.persons;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const personName = t3;
  const i = statuses.indexOf(status);
  const t4 = `col:${status}`;
  let t5;
  if ($[4] !== color) {
    t5 = <Dot t="dot" color={color} />;
    $[4] = color;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== status || $[7] !== t5) {
    t6 = <span data-t="column-title">{t5}{status}</span>;
    $[6] = status;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== tasks.length) {
    t7 = <span data-t="count">{tasks.length}</span>;
    $[9] = tasks.length;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t6 || $[12] !== t7) {
    t8 = <h2 data-t="column-head">{t6}{t7}</h2>;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== i || $[15] !== onOpen || $[16] !== personName || $[17] !== projectName || $[18] !== selected || $[19] !== statuses || $[20] !== t || $[21] !== tasks) {
    let t10;
    if ($[23] !== i || $[24] !== onOpen || $[25] !== personName || $[26] !== projectName || $[27] !== selected || $[28] !== statuses || $[29] !== t) {
      t10 = task => <Card key={task.id} task={task} current={selected === task.id} blocked={!!task.blocked} project={projectName.get(task.projectId) ?? ""} who={task.assigneeId ? personName.get(task.assigneeId) ?? "" : t("board.unassigned")} left={statuses[i - 1] ?? null} right={statuses[i + 1] ?? null} onOpen={onOpen} />;
      $[23] = i;
      $[24] = onOpen;
      $[25] = personName;
      $[26] = projectName;
      $[27] = selected;
      $[28] = statuses;
      $[29] = t;
      $[30] = t10;
    } else {
      t10 = $[30];
    }
    t9 = tasks.map(t10);
    $[14] = i;
    $[15] = onOpen;
    $[16] = personName;
    $[17] = projectName;
    $[18] = selected;
    $[19] = statuses;
    $[20] = t;
    $[21] = tasks;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[31] !== t9) {
    t10 = <div className="cards" data-t="cards">{t9}</div>;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== status || $[34] !== t10 || $[35] !== t4 || $[36] !== t8) {
    t11 = <section className="col" data-t="column" data-col={status} data-page={t4} aria-label={status}>{t8}{t10}</section>;
    $[33] = status;
    $[34] = t10;
    $[35] = t4;
    $[36] = t8;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  return t11;
}
function _temp2(p_0) {
  return [p_0.id, p_0.name];
}
function _temp(p) {
  return [p.id, p.name];
}
type CardProps = {
  task: Task;
  current: boolean;
  blocked: boolean;
  project: string;
  who: string;
  left: TaskStatus | null;
  right: TaskStatus | null;
  onOpen: (id: string) => void;
};
const Card = memo(function Card(t0) {
  const $ = _c(38);
  const {
    task,
    current,
    blocked,
    project,
    who,
    left,
    right,
    onOpen
  } = t0;
  const cover = useBlobUrl(task.cover);
  const move = useData(_temp3);
  const t1 = blocked ? "card blocked" : "card";
  const t2 = task.id;
  let t3;
  if ($[0] !== onOpen || $[1] !== task.id) {
    t3 = () => onOpen(task.id);
    $[0] = onOpen;
    $[1] = task.id;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== left || $[4] !== move || $[5] !== onOpen || $[6] !== right || $[7] !== task.id) {
    t4 = e => {
      const next = e.shiftKey ? e.key === "ArrowLeft" ? left : e.key === "ArrowRight" ? right : null : null;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onOpen(task.id);
      } else {
        if (next) {
          e.preventDefault();
          move(task.id, next);
        }
      }
    };
    $[3] = left;
    $[4] = move;
    $[5] = onOpen;
    $[6] = right;
    $[7] = task.id;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== cover || $[10] !== task.cover) {
    t5 = task.cover && <img className="cover" data-t="card-cover" src={cover ?? undefined} width={480} height={240} loading="lazy" decoding="async" alt="" draggable={false} />;
    $[9] = cover;
    $[10] = task.cover;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== task.title) {
    t6 = <div className="title" data-t="card-title">{task.title}</div>;
    $[12] = task.title;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== task.id) {
    t7 = local(task.id);
    $[14] = task.id;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t7) {
    t8 = <span>{t7}</span>;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== task.instance) {
    t9 = <InstanceTag instance={task.instance} />;
    $[18] = task.instance;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== project || $[21] !== t9) {
    t10 = <span>{project} {t9}</span>;
    $[20] = project;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== who) {
    t11 = <span>{who}</span>;
    $[23] = who;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11 || $[27] !== t8) {
    t12 = <div className="meta mono" data-t="card-meta">{t8}{t10}{t11}</div>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t8;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== current || $[30] !== t1 || $[31] !== t12 || $[32] !== t3 || $[33] !== t4 || $[34] !== t5 || $[35] !== t6 || $[36] !== task.id) {
    t13 = <div role="button" tabIndex={0} className={t1} data-t="card" data-id={t2} aria-current={current} onClick={t3} onKeyDown={t4}>{t5}{t6}{t12}</div>;
    $[29] = current;
    $[30] = t1;
    $[31] = t12;
    $[32] = t3;
    $[33] = t4;
    $[34] = t5;
    $[35] = t6;
    $[36] = task.id;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  return t13;
});
function _temp3(s) {
  return s.move;
}